import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './scss/Comments.scss';
import ImagePlaceholder from '../Global/ImagePlaceholder';
import { timeAgo } from '../../utils/helpers';
import { config } from '../../config';
import { resetMentionedIds } from "../../redux/Slices/mentionedSlice";
import { fetchActivities, updateCommentOptimistically, addReplyOptimistically, updateReplyOptimistically, deleteReplyOptimistically, setCommentUpdatingState, setHighlightTarget, clearHighlightTarget } from '../../redux/Slices/activitiesSlice';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import Editor from '../LexicalEditor/Editor';
import LinkPreviewWrapper from '../Global/LinkPreviewWrapper';
import { togglePopover } from '../../redux/Slices/popoverSlice';

const Comment = ({ activity, cardId, isReadOnly = false, highlightTarget }) => {
    const { user } = useSelector((state) => state.user);
    const dispatch = useDispatch();
    const mentionedIds = useSelector((state) => state.mentioned.mentionedMemberIds);

    const [comment, setComment] = useState(activity.details); // Initialize with activity details
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editingComment, setEditingComment] = useState('');


    // Reply and reaction states
    const [showReplyInput, setShowReplyInput] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [showReplies, setShowReplies] = useState(false);
    const [editingReplyId, setEditingReplyId] = useState(null);
    const [editingReplyText, setEditingReplyText] = useState('');
    const reactionButtonRef = useRef(null);
    const rootRef = useRef(null);

    // Use commentData if available, otherwise fall back to activity data
    const commentData = activity.commentData || activity;
    const [reactions, setReactions] = useState(commentData.reactions || activity.reactions || []);
    const [replies, setReplies] = useState(commentData.repliesData || []);

    // Update reactions when activity changes
    useEffect(() => {
        const newReactions = commentData.reactions || activity.reactions || [];
        setReactions(newReactions);

        const newReplies = commentData.repliesData || [];
        setReplies(newReplies);
    }, [activity, commentData]);

    const { handlePopoverClick } = useHandlePopoverClick();


    // Ensure comment is updated if it's a new comment
    useEffect(() => {
        if (activity.actionType === 'ADDED_COMMENT') {
            setComment(activity.details);
        }
    }, [activity]);
    // Highlight when navigated from notification or just created
    useEffect(() => {
        if (!highlightTarget) return;
        // Highlight comment
        if (highlightTarget.type === 'comment' && (activity.actionTypeId === highlightTarget.id || (activity.commentData && activity.commentData._id === highlightTarget.id))) {
            const el = document.getElementById(`comment-${activity._id}`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const bubble = el.querySelector('.card-comment-content');
                if (bubble) {
                    bubble.classList.add('is-new');
                    setTimeout(() => bubble.classList.remove('is-new'), 2000);
                }
                dispatch(clearHighlightTarget());
            }
        }
    }, [highlightTarget, activity]);

    useEffect(() => {
        if (!highlightTarget || highlightTarget.type !== 'reply') return;
        const targetId = highlightTarget.id;
        const found = replies?.some(r => r._id === targetId);
        if (found) {
            setShowReplies(true);
            setTimeout(() => {
                const el = document.getElementById(`reply-${targetId}`);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    const bubble = el.querySelector('.card-comment-content');
                    if (bubble) {
                        bubble.classList.add('is-new');
                        setTimeout(() => bubble.classList.remove('is-new'), 2000);
                    }
                    dispatch(clearHighlightTarget());
                }
            }, 0);
        }
    }, [highlightTarget, replies]);


    const handleUpdateComment = async () => {
        if (!editingComment.trim()) return;

        const originalComment = comment;
        const originalEditingComment = editingComment;

        // Dispatch optimistic update (will show "updating..." state)
        dispatch(updateCommentOptimistically({
            id: activity._id,
            updatedDetails: editingComment,
        }));

        // Update local state immediately for smooth UX
        setComment(editingComment);
        setEditingCommentId(null);
        setEditingComment('');

        try {
            const response = await fetch(`${config.API_URI}/v1/cards/${cardId}/comments/${activity.actionTypeId}`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    comment: originalEditingComment,
                    activityId: activity._id,
                    initiatorId: user?.user?._id,
                    mentionedIds
                })
            });

            if (!response.ok) throw new Error('Failed to update comment');

            // Remove updating state
            dispatch(setCommentUpdatingState({ id: activity._id, isUpdating: false }));

            // Fetch fresh data from server
            dispatch(fetchActivities({ cardId }));
            dispatch(resetMentionedIds());
        } catch (error) {
            console.error('Error updating comment:', error);
            // Revert optimistic update on error
            dispatch(updateCommentOptimistically({
                id: activity._id,
                updatedDetails: originalComment,
            }));
            dispatch(setCommentUpdatingState({ id: activity._id, isUpdating: false }));

            // Restore editing state
            setComment(originalComment);
            setEditingCommentId(activity._id);
            setEditingComment(originalEditingComment);
        }
    };


    // Handle comment deletion
    const handleDeleteComment = (e, activityId) => {
        const apiUrl = `${config.API_URI}/v1/cards/${cardId}/comments/${activity.actionTypeId}`;
        handlePopoverClick(e, 'deleteAction', { type: 'COMMENT', id: activityId, url: apiUrl });
    };

    // Handle adding a reply
    const handleAddReply = async () => {
        if (!replyText.trim()) return;

        const tempReplyId = `temp-${Date.now()}`;
        const originalReplyText = replyText;

        // Optimistic update - create reply object that matches server structure
        const optimisticReply = {
            _id: tempReplyId,
            comment: replyText,
            member: user?.user,
            time: new Date().toISOString(),
            reactions: []
        };

        // Dispatch optimistic update (will show "posting..." state)
        dispatch(addReplyOptimistically({
            reply: optimisticReply,
            parentCommentId: activity.actionTypeId
        }));

        // Clear input immediately for smooth UX
        setReplyText('');
        setShowReplies(true); // Show replies section

        try {
            const response = await fetch(`${config.API_URI}/v1/cards/${cardId}/comments/${activity.actionTypeId}/replies`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    comment: originalReplyText,
                    member: user?.user?._id
                })
            });

            if (!response.ok) throw new Error('Failed to add reply');

            const data = await response.json();
            if (data?.replyId) {
                // Mark this reply to be highlighted after fetch
                dispatch(setHighlightTarget({ type: 'reply', id: data.replyId }));
            }

            // Fetch fresh data from server to get the actual reply
            const res = await dispatch(fetchActivities({ cardId }));

            if (res.meta.requestStatus === 'fulfilled') {
                dispatch(deleteReplyOptimistically(tempReplyId));
            }

        } catch (error) {
            console.error('Error adding reply:', error);
            // Remove optimistic update on error
            dispatch(deleteReplyOptimistically(tempReplyId));
            // Restore the input
            setReplyText(originalReplyText);
        }
    };

    // Handle updating a reply
    const handleUpdateReply = async (replyId) => {
        if (!editingReplyText.trim()) return;

        const originalText = editingReplyText;

        // Find the current reply to get its original content for potential revert
        let currentReplyContent = '';
        replies.forEach(reply => {
            if (reply._id === replyId) {
                currentReplyContent = reply.comment;
            }
        });

        // Optimistic update (will show "updating..." state)
        dispatch(updateReplyOptimistically({
            replyId: replyId,
            updatedDetails: editingReplyText,
            isUpdating: true
        }));

        setEditingReplyId(null);
        setEditingReplyText('');

        try {
            const response = await fetch(`${config.API_URI}/v1/cards/${cardId}/comments/${activity.actionTypeId}/replies/${replyId}`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    comment: originalText
                })
            });

            if (!response.ok) throw new Error('Failed to update reply');

            // Remove updating state
            dispatch(updateReplyOptimistically({
                replyId: replyId,
                isUpdating: false
            }));

            // Fetch fresh data from server
            dispatch(fetchActivities({ cardId }));
        } catch (error) {
            console.error('Error updating reply:', error);
            // Revert optimistic update on error
            dispatch(updateReplyOptimistically({
                replyId: replyId,
                updatedDetails: currentReplyContent,
                isUpdating: false
            }));
            // Restore editing state
            setEditingReplyId(replyId);
            setEditingReplyText(originalText);
        }
    };

    // Handle deleting a reply
    const handleDeleteReply = (e, replyId) => {
        const apiUrl = `${config.API_URI}/v1/cards/${cardId}/comments/${activity.actionTypeId}/replies/${replyId}`;
        console.log({ replyId, apiUrl });

        handlePopoverClick(e, 'deleteAction', { type: 'REPLY', id: replyId, url: apiUrl, cardId });
    };

    // Handle reply reaction toggle
    const handleReplyReactionToggle = async (replyId, emoji, currentReactions) => {
        // Optimistic update
        const currentUserReaction = currentReactions.find(r => {
            const reactionUserId = typeof r.user === 'object' ? r.user._id : r.user;
            return reactionUserId === user?.user?._id && r.emoji === emoji;
        });

        let newReactions;
        if (currentUserReaction) {
            // Remove reaction
            newReactions = currentReactions.filter(r => {
                const reactionUserId = typeof r.user === 'object' ? r.user._id : r.user;
                return !(reactionUserId === user?.user?._id && r.emoji === emoji);
            });
        } else {
            // Add reaction
            newReactions = [...currentReactions, {
                user: user?.user,
                emoji: emoji,
                createdAt: new Date().toISOString()
            }];
        }

        // Update the reply reactions optimistically in Redux
        dispatch(updateReplyOptimistically({
            replyId: replyId,
            reactions: newReactions
        }));

        try {
            const response = await fetch(`${config.API_URI}/v1/cards/${cardId}/comments/${activity.actionTypeId}/replies/${replyId}/reactions`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ emoji })
            });

            if (!response.ok) throw new Error('Failed to toggle reply reaction');

            const result = await response.json();
            // Update with server response
            dispatch(updateReplyOptimistically({
                replyId: replyId,
                reactions: result.reactions
            }));
        } catch (error) {
            console.error('Error toggling reply reaction:', error);
            // Revert optimistic update on error
            dispatch(updateReplyOptimistically({
                replyId: replyId,
                reactions: currentReactions
            }));
        }
    };

    // Handle emoji selection for replies
    const handleReplyEmojiSelect = (replyId, currentReactions) => (emojiData) => {
        const emoji = emojiData.native;
        handleReplyReactionToggle(replyId, emoji, currentReactions);
        // Close the popover after selection
        dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
    };

    // Handle emoji selection from picker
    const handleEmojiSelect = (emojiData) => {
        const emoji = emojiData.native;
        handleReactionToggle(emoji);
        // Close the popover after selection
        dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
    };

    // Handle reaction toggle
    const handleReactionToggle = async (emoji) => {


        // Optimistic update
        const currentUserReaction = reactions.find(r => {
            const reactionUserId = typeof r.user === 'object' ? r.user._id : r.user;
            return reactionUserId === user?.user?._id && r.emoji === emoji;
        });
        let newReactions;

        if (currentUserReaction) {
            // Remove reaction
            newReactions = reactions.filter(r => {
                const reactionUserId = typeof r.user === 'object' ? r.user._id : r.user;
                return !(reactionUserId === user?.user?._id && r.emoji === emoji);
            });
        } else {
            // Add reaction
            newReactions = [...reactions, {
                user: user?.user,
                emoji: emoji,
                createdAt: new Date().toISOString()
            }];
        }

        setReactions(newReactions);


        try {
            const response = await fetch(`${config.API_URI}/v1/cards/${cardId}/comments/${activity.actionTypeId}/reactions`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',

                },
                credentials: 'include',
                body: JSON.stringify({ emoji })
            });

            if (!response.ok) throw new Error('Failed to toggle reaction');

            const result = await response.json();
            setReactions(result.reactions);
        } catch (error) {
            console.error('Error toggling reaction:', error);
            // Revert optimistic update on error
            setReactions(reactions);
        }

        // Note: No need to setShowReactionPicker since we're using popover system now
    };

    // Group reactions by emoji
    const groupedReactions = reactions.reduce((acc, reaction) => {
        if (!acc[reaction.emoji]) {
            acc[reaction.emoji] = {
                emoji: reaction.emoji,
                count: 0,
                users: [],
                currentUserReacted: false
            };
        }
        acc[reaction.emoji].count++;
        acc[reaction.emoji].users.push(reaction.user);

        // Handle both populated user objects and user IDs
        const reactionUserId = typeof reaction.user === 'object' ? reaction.user._id : reaction.user;
        if (reactionUserId === user?.user?._id) {
            acc[reaction.emoji].currentUserReacted = true;
        }
        return acc;
    }, {});


    return (
        <div className="comment-section" id={`comment-${activity._id}`}>
            <div className="comment">
                {editingCommentId !== activity._id && (
                    <>
                        {activity.initiator?.profilePicture ? (
                            <img
                                src={activity.initiator.profilePicture}
                                alt={activity.initiator.name}
                                className="comment__profile-img"
                                loading="lazy"
                            />
                        ) : (
                            <ImagePlaceholder size={35} text={activity.initiator.name} onlineIndicator={false} />
                        )}
                    </>
                )}


                <div className={`comment__content ${editingCommentId === activity._id ? 'editing-comment' : ''}`}>
                    <div className="comment__header">
                        {editingCommentId === activity._id && (
                            <>
                                {
                                    activity.initiator?.profilePicture ? (
                                        <img
                                            src={activity.initiator.profilePicture}
                                            alt={activity.initiator.name}
                                            className="comment__profile-img"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <ImagePlaceholder size={20} text={activity.initiator.name} onlineIndicator={false} />
                                    )
                                }
                            </>

                        )}
                        <strong>{activity.initiator.name}</strong>
                        <span>{timeAgo(activity.createdAt)}</span>
                    </div>

                    {editingCommentId === activity._id ? (
                        <Editor
                            value={editingComment}
                            setDescription={setEditingComment}
                            setIsEditingDescription={setEditingCommentId}
                            handleUpdateCard={handleUpdateComment}
                            cardId={cardId}
                            context="comment"

                        />

                    ) : (
                        <LinkPreviewWrapper className="card-comment-content" dangerouslySetInnerHTML={{ __html: comment }} />
                    )}

                    {/* Show loading states */}
                    {activity.isPosting && (
                        <div className="comment__loading">Posting...</div>
                    )}
                    {activity.isUpdating && (
                        <div className="comment__loading">Updating...</div>
                    )}

                    {!activity.isPosting && !activity.isUpdating && (
                        <>
                            {/* Reactions Display */}
                            {Object.keys(groupedReactions).length > 0 && (
                                <div className="comment__reactions">
                                    {Object.values(groupedReactions).map((reaction) => (
                                        <span
                                            key={reaction.emoji}
                                            className={`comment__reaction ${reaction.currentUserReacted ? 'reacted' : ''}`}
                                            onClick={() => !isReadOnly && handleReactionToggle(reaction.emoji)}
                                            style={{ cursor: isReadOnly ? 'default' : 'pointer' }}
                                            data-tooltip-content={reaction.users.map(u => u.name).join(', ')}
                                            data-tooltip-position="bottom"
                                        >
                                            {reaction.emoji} {reaction.count}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* Action buttons - hide when posting or updating or read-only */}
                            {!activity.isPosting && !activity.isUpdating && !isReadOnly && (
                                <div className="comment__actions comment__actions--minimal">
                                    <div className="comment__reaction-button-container">
                                        <span
                                            ref={reactionButtonRef}
                                            className="material-symbols-outlined comment__action-link"
                                            id={`emoji-picker-button-${activity.actionTypeId}`}
                                            onClick={(e) => handlePopoverClick(e, 'emojiPicker', {
                                                onEmojiSelect: handleEmojiSelect,
                                                isClosed: false
                                            })}
                                        >
                                            add_reaction
                                        </span>
                                    </div>
                                    <span className="comment__dot">●</span>

                                    <span
                                        className="comment__action-link reply"
                                        onClick={() => setShowReplyInput(!showReplyInput)}
                                    >
                                        Reply
                                    </span>
                                    {(user?.user?._id === activity.initiator._id) && (
                                        <>
                                            <span className="comment__dot">●</span>
                                            <span
                                                className="comment__action-link edit"
                                                onClick={() => {
                                                    setEditingCommentId(activity._id);
                                                    setEditingComment(comment);
                                                }}
                                            >
                                                Edit
                                            </span>
                                            <span className="comment__dot">●</span>
                                            <span className="comment__action-link delete" id={`popover-delete-comment-${activity._id}`} onClick={(e) => handleDeleteComment(e, activity._id)} data-popover-trigger>Delete</span>
                                        </>
                                    )}
                                </div>
                            )}
                        </>
                    )}


                    {/* Reply Input */}
                    {showReplyInput && (
                        <div className="comment__reply-input">
                            <Editor
                                value={replyText}
                                setDescription={setReplyText}
                                setIsEditingDescription={setShowReplyInput}
                                handleUpdateCard={handleAddReply}
                                cardId={cardId}
                                placeholder="Write a reply..."
                                context="reply"
                            />
                        </div>
                    )}

                    {/* Replies Display */}
                    {replies && replies.length > 0 && (
                        <div className="comment__replies">
                            {!showReplies ? (
                                <span
                                    className="comment__show-replies"
                                    onClick={() => setShowReplies(true)}
                                >
                                    Show {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                                </span>
                            ) : (
                                <span
                                    className="comment__hide-replies"
                                    onClick={() => setShowReplies(false)}
                                >
                                    Hide replies
                                </span>
                            )}

                            {showReplies && (
                                <div className="comment__replies-container">

                                    {replies.map((reply) => (
                                        <div key={reply._id} id={`reply-${reply._id}`} className="comment__reply">
                                            <div className="comment">
                                                {reply.member?.profilePicture ? (
                                                    <img
                                                        src={reply.member.profilePicture}
                                                        alt={reply.member.name}
                                                        className="comment__profile-img"
                                                        loading="lazy"
                                                    />
                                                ) : (
                                                    <ImagePlaceholder size={35} text={reply.member?.name} onlineIndicator={false} />
                                                )}

                                                <div className="comment__content">
                                                    <div className="comment__header">
                                                        <strong>{reply.member?.name}</strong>
                                                        <span>{timeAgo(reply.time)}</span>
                                                    </div>

                                                    {editingReplyId === reply._id ? (
                                                        <Editor
                                                            value={editingReplyText}
                                                            setDescription={setEditingReplyText}
                                                            setIsEditingDescription={setEditingReplyId}
                                                            handleUpdateCard={() => handleUpdateReply(reply._id)}
                                                            cardId={cardId}
                                                            context="reply"
                                                        />
                                                    ) : (
                                                        <>
                                                            <LinkPreviewWrapper className={`card-comment-content${reply.isUpdating || reply.isPosting ? ' full-deactivated' : ''}`} dangerouslySetInnerHTML={{ __html: reply.comment }} />

                                                            {/* Show loading states for replies */}
                                                            {reply.isPosting && (
                                                                <div className="comment__loading">Posting...</div>
                                                            )}
                                                            {reply.isUpdating && (
                                                                <div className="comment__loading">Updating...</div>
                                                            )}

                                                            {/* Reply reactions */}
                                                            {reply.reactions && reply.reactions.length > 0 && (
                                                                <div className="comment__reactions">
                                                                    {Object.values(reply.reactions.reduce((acc, reaction) => {
                                                                        if (!acc[reaction.emoji]) {
                                                                            acc[reaction.emoji] = {
                                                                                emoji: reaction.emoji,
                                                                                count: 0,
                                                                                users: [],
                                                                                currentUserReacted: false
                                                                            };
                                                                        }
                                                                        acc[reaction.emoji].count++;
                                                                        acc[reaction.emoji].users.push(reaction.user);

                                                                        // Check if current user reacted
                                                                        const reactionUserId = typeof reaction.user === 'object' ? reaction.user._id : reaction.user;
                                                                        if (reactionUserId === user?.user?._id) {
                                                                            acc[reaction.emoji].currentUserReacted = true;
                                                                        }
                                                                        return acc;
                                                                    }, {})).map((reaction) => (
                                                                        <span
                                                                            key={reaction.emoji}
                                                                            className={`comment__reaction ${reaction.currentUserReacted ? 'reacted' : ''}`}
                                                                            onClick={() => !isReadOnly && handleReplyReactionToggle(reply._id, reaction.emoji, reply.reactions)}
                                                                            style={{ cursor: isReadOnly ? 'default' : 'pointer' }}
                                                                            data-tooltip-content={reaction.users.map(u => u.name || u.username).join(', ')}
                                                                            data-tooltip-position="bottom"
                                                                        >
                                                                            {reaction.emoji} {reaction.count}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {/* Reply actions - hide when posting or updating or read-only */}
                                                            {!reply.isPosting && !reply.isUpdating && !isReadOnly && (
                                                                <div className="comment__actions comment__actions--minimal">
                                                                    <div className="comment__reaction-button-container">
                                                                        <span
                                                                            className="material-symbols-outlined comment__action-link"
                                                                            id={`emoji-picker-button-reply-${reply._id}`}
                                                                            onClick={(e) => handlePopoverClick(e, 'emojiPicker', {
                                                                                onEmojiSelect: handleReplyEmojiSelect(reply._id, reply.reactions),
                                                                                isClosed: false
                                                                            })}
                                                                        >
                                                                            add_reaction
                                                                        </span>
                                                                    </div>
                                                                    {(user?.user?._id === reply.member?._id) && (
                                                                        <>
                                                                            <span className="comment__dot">●</span>
                                                                            <span
                                                                                className="comment__action-link edit"
                                                                                onClick={() => {
                                                                                    setEditingReplyId(reply._id);
                                                                                    setEditingReplyText(reply.comment);
                                                                                }}
                                                                            >
                                                                                Edit
                                                                            </span>
                                                                            <span className="comment__dot">●</span>
                                                                            <span
                                                                                className="comment__action-link delete"
                                                                                id={`popover-delete-reply-${reply._id}`}
                                                                                onClick={(e) => handleDeleteReply(e, reply._id)}
                                                                                data-popover-trigger
                                                                            >
                                                                                Delete
                                                                            </span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default Comment;
